import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import AiIcon from '../assets/ai/AiIcon';
import AiIconClose from '../assets/ai/AiIconClose';
import AiHeader from './AiHeader';
import ChatInput from './ChatInput';
import MessageBox from './MessageBox/MessageBox';
import WelcomeBubble from './WelcomeBubble';
import useAiTutor from './hooks';

const mountedStyle = { animation: 'bounceIn 250ms ease-in' };

const unmountedStyle = {
  animation: 'bounceOut 270ms ease-out',
  animationFillMode: 'forwards',
};

const AiTutorDesktop = ({ courseId, pendingMessage, onPendingMessageConsumed }) => {
  // prettier-ignore
  const { isOpen, showDiv, messages, isSending, sendMessage, toggleOpen, chatEndRef } =
      useAiTutor(courseId);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(false);
  const [isClosingBubble, setIsClosingBubble] = useState(false);

  useEffect(() => {
    if (!pendingMessage) return;
    setShowWelcomeBubble(false);
    if (!isOpen) toggleOpen();
    const formData = new FormData();
    formData.append('message', pendingMessage);
    sendMessage(formData);
    onPendingMessageConsumed();
  }, [pendingMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcomeBubble(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTutorClose = () => {
    toggleOpen();
    setIsExpanded(!isExpanded);
  };

  const handleIconClick = () => {
    setIsClosingBubble(true);
    setTimeout(() => {
      setShowWelcomeBubble(false);
      setIsClosingBubble(false);
      toggleOpen();
    }, 600);
  };

  const handleCloseBubble = () => {
    setIsClosingBubble(true);
    setTimeout(() => {
      setShowWelcomeBubble(false);
      setIsClosingBubble(false);
    }, 600);
  };

  return (
    <div
      className={`ai-tutor tw-fixed tw-bottom-0 tw-right-0 tw-z-50 ${isExpanded ? 'tw-mb-4 tw-mr-4' : 'tw-mb-8 tw-mr-11'} tw-flex tw-items-end ${showWelcomeBubble ? 'tw-flex-row tw-gap-3' : 'tw-flex-col'}`}
    >
      {showDiv && (
        <div
          className={`tw-flex tw-flex-col tw-rounded-md tw-bg-white tw-shadow-lg tw-transition-all tw-duration-300 ${
            isExpanded ? 'tw-h-[calc(100vh-80px)] tw-w-[50vw]' : 'tw-mb-3 tw-h-[480px] tw-w-[360px]'
          }`}
          style={isOpen ? mountedStyle : unmountedStyle}
        >
          <AiHeader
            isExpanded={isExpanded}
            toggleExpand={toggleExpand}
            handleTutorClose={handleTutorClose}
          />
          <MessageBox
            queryData={messages}
            isLoading={isSending}
            chatEndRef={chatEndRef}
            isExpanded={isExpanded}
            sendMessage={sendMessage}
          />
          <ChatInput mutate={sendMessage} className="tw-mt-auto" />
        </div>
      )}

      {!showDiv && showWelcomeBubble && (
        <WelcomeBubble
          isClosing={isClosingBubble}
          onClose={handleCloseBubble}
          onClick={handleIconClick}
        />
      )}

      {!isExpanded && (
        <button
          type="button"
          onClick={handleIconClick}
          className={`ai-button tw-relative ${isOpen ? 'toggle-open' : 'toggle-close'} tw-h-[64px] tw-w-[64px]`}
        >
          {showDiv ? (
            <AiIconClose />
          ) : (
            <>
              <AiIcon className="ai-pulse" width={64} height={64} />
              {showWelcomeBubble && !isClosingBubble && (
                <span className="tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-mainPurple tw-text-[14px] tw-font-medium tw-text-white">
                  1
                </span>
              )}
            </>
          )}
        </button>
      )}
    </div>
  );
};

AiTutorDesktop.propTypes = {
  courseId: PropTypes.string.isRequired,
  pendingMessage: PropTypes.string,
  onPendingMessageConsumed: PropTypes.func,
};

AiTutorDesktop.defaultProps = {
  pendingMessage: '',
  onPendingMessageConsumed: () => {},
};

export default AiTutorDesktop;
